export interface ScenarioItem {
  id: string;
  scenario: string;
  scenarioTag: string;
  icon: string;
  xmValue: string;
  xmDetail: string;
  fxproValue: string;
  fxproDetail: string;
  winner: "xm" | "fxpro" | "tie";
  reason: string;
}

export const scenarioData: ScenarioItem[] = [
  {
    id: "scalping",
    scenario: "Heavy Scalping / High-Frequency",
    scenarioTag: "Scalping",
    icon: "ri-flashlight-line",
    xmValue: "EUR/USD spread ~1.0 (Standard), 0.6 (Ultra Low)",
    xmDetail: "Zero commission on Standard and Ultra Low accounts. Reliable execution with 99.35% fill rate. But no cTrader — scalpers who rely on Level 2 depth and advanced DOM are limited to MT4/MT5.",
    fxproValue: "EUR/USD avg 0.0-0.2 pips (Raw+), cTrader with full DOM",
    fxproDetail: "Raw+ account with true ECN execution through cTrader — full Depth of Market, iceberg orders, and one-click scalping. $3.50/side commission. The cTrader scalping ecosystem is unmatched by anything XM offers.",
    winner: "fxpro",
    reason: "FxPro Wins for Serious Scalpers: cTrader with Level 2 DOM + Raw+ spreads from 0.0 pips is a scalper's dream setup. XM's MT4-only approach and wider spreads can't match this. At 10+ lots/day, FxPro's tooling advantage is decisive."
  },
  {
    id: "beginner",
    scenario: "Small Capital / Getting Started",
    scenarioTag: "Beginners",
    icon: "ri-seedling-line",
    xmValue: "$30 no-deposit bonus + $5 minimum deposit",
    xmDetail: "$30 free to trade with right after verification. 100% welcome bonus up to $500 doubles your first deposit. Massive educational library with live webinars. Start with pocket change and trade with broker's money.",
    fxproValue: "No bonuses, $100 minimum deposit",
    fxproDetail: "Standard account starts at $100. No welcome bonuses, no deposit match, no no-deposit promotions. FxPro's philosophy is pure execution quality — they don't compete on giveaways. Educational resources are solid but less beginner-oriented.",
    winner: "xm",
    reason: "XM Wins by a Landslide: $5 entry + $30 free + 100% deposit match. For someone testing forex with $100 or less, XM is the only choice. FxPro's $100 minimum and zero bonuses create a much higher barrier."
  },
  {
    id: "swing",
    scenario: "Large Capital / Swing & Hold",
    scenarioTag: "Swing Trading",
    icon: "ri-moon-line",
    xmValue: "ASIC/CySEC regulated, standard swaps, $5 entry",
    xmDetail: "Multi-regulated with solid investor protection. Standard overnight swap fees at industry average. Negative balance protection across all jurisdictions. Reliable for multi-week and multi-month position holds.",
    fxproValue: "FCA regulated, institutional liquidity, deep order book",
    fxproDetail: "UK FCA regulation with FSCS protection up to £85,000. Institutional-grade liquidity from 15+ Tier-1 banks means minimal slippage on large positions (50+ lots). FxPro serves hedge funds and family offices — it's built for big money.",
    winner: "fxpro",
    reason: "FxPro Wins for Serious Capital: FCA + FSCS + institutional liquidity pool. When you're swinging 20 standard lots of EUR/USD, execution quality and fund safety matter more than bonuses. FxPro's infrastructure is built for this use case."
  },
  {
    id: "news",
    scenario: "News Trading / High-Impact Events",
    scenarioTag: "News Events",
    icon: "ri-newspaper-line",
    xmValue: "99.35% execution rate, near-zero requotes",
    xmDetail: "99.35% of orders executed in under 1 second. Explicit no-requote guarantee even during NFP and FOMC. Rock-solid execution with strict slippage control during black-swan events.",
    fxproValue: "Institutional fills, variable slippage during extremes",
    fxproDetail: "Excellent execution under normal conditions with institutional-grade liquidity. During extreme news events (NFP surprises, flash crashes), spreads widen and fills may slip 2-5 pips as market depth thins. cTrader's DOM shows exact liquidity in real-time.",
    winner: "xm",
    reason: "XM Wins — Fear No NFP: XM's explicit no-requote guarantee + proven 99.35% fill rate during high-impact events is a safety net news traders can count on. FxPro's ECN model is transparent but can't promise zero slippage when liquidity evaporates."
  }
];

export interface BrokerHonestTake {
  pros: { text: string; icon: string }[];
  cons: { text: string; icon: string }[];
}

export const xmHonestTake: BrokerHonestTake = {
  pros: [
    {
      text: "$30 no-deposit bonus + 100% welcome bonus up to $500. Trade with the broker's money before risking your own.",
      icon: "ri-check-line"
    },
    {
      text: "99.35% order execution rate in under 1 second. Virtually zero requotes even during major news events.",
      icon: "ri-check-line"
    },
    {
      text: "Ultra Low account with zero commissions and spreads from 0.6 pips. Pure spread model, no hidden fees.",
      icon: "ri-check-line"
    },
    {
      text: "ASIC, CySEC, FSC multi-regulated. Segregated client funds and negative balance protection for maximum security.",
      icon: "ri-check-line"
    },
    {
      text: "Free VPS for EA traders + XMP loyalty points on every trade. Trade more, earn back more.",
      icon: "ri-check-line"
    }
  ],
  cons: [
    {
      text: "Standard account spreads are average at 1.0 pip. Without bonuses, XM isn't the cheapest on raw spread cost alone.",
      icon: "ri-emotion-sad-line"
    },
    {
      text: "No cTrader support. Scalpers and professional traders who rely on Depth of Market and advanced order types are limited to MT4/MT5.",
      icon: "ri-stock-line"
    },
    {
      text: "Bonus terms come with trading volume requirements. The free money isn't withdrawable until you trade enough lots.",
      icon: "ri-file-list-3-line"
    }
  ]
};

export const fxproHonestTake: BrokerHonestTake = {
  pros: [
    {
      text: "cTrader platform with full Depth of Market, iceberg orders, and one-click trading. The industry gold standard for professional scalping.",
      icon: "ri-check-line"
    },
    {
      text: "UK FCA regulation since 2006 with FSCS protection up to £85,000. One of the most established and trusted brokers in the industry.",
      icon: "ri-check-line"
    },
    {
      text: "Institutional-grade liquidity from 15+ Tier-1 banks including Barclays, JPMorgan, and UBS. Massive order book means tight fills at any size.",
      icon: "ri-check-line"
    },
    {
      text: "FxPro Edge — proprietary platform with advanced charting, custom indicators, and multi-asset trading in one interface.",
      icon: "ri-check-line"
    },
    {
      text: "No restrictions on trading strategies. Scalping, hedging, algorithmic trading, and news trading all fully permitted on all accounts.",
      icon: "ri-check-line"
    }
  ],
  cons: [
    {
      text: "Zero bonuses, zero deposit promotions, zero loyalty rewards. FxPro doesn't believe in giveaways — you pay for execution quality and nothing else.",
      icon: "ri-alert-line"
    },
    {
      text: "$100 minimum deposit across all accounts. Significantly higher barrier than XM's $5. Beginners with small budgets are effectively priced out.",
      icon: "ri-money-dollar-circle-line"
    },
    {
      text: "Spreads widen noticeably during major news events (NFP, FOMC). ECN model is transparent but can't match XM's guaranteed execution during chaos.",
      icon: "ri-time-line"
    }
  ]
};

export interface AccountMatchup {
  id: string;
  title: string;
  subtitle: string;
  xmAccount: {
    name: string;
    spread: string;
    commission: string;
    minDeposit: string;
    leverage: string;
    highlight: string;
    pros: string[];
  };
  fxproAccount: {
    name: string;
    spread: string;
    commission: string;
    minDeposit: string;
    leverage: string;
    highlight: string;
    pros: string[];
  };
  analysis: string;
}

export const accountMatchups: AccountMatchup[] = [
  {
    id: "lowcost",
    title: "The Low-Cost Battle: Who Gives You More Per Dollar?",
    subtitle: "XM Ultra Low Account vs FxPro Raw+ Account — pure economics, no marketing spin",
    xmAccount: {
      name: "XM Ultra Low",
      spread: "EUR/USD from 0.6 pips",
      commission: "Zero commission",
      minDeposit: "$50",
      leverage: "1:1000",
      highlight: "Zero commission + tight spreads",
      pros: ["Zero commission pure spread model", "Lowest cost for swing & medium-frequency", "$50 entry barrier", "1,000+ instruments"]
    },
    fxproAccount: {
      name: "FxPro Raw+",
      spread: "EUR/USD from 0.0 pips, avg 0.1-0.2",
      commission: "$3.50/side ($7/lot round-turn)",
      minDeposit: "$500",
      leverage: "1:500",
      highlight: "True ECN with cTrader Level 2 depth",
      pros: ["cTrader with full Depth of Market", "Raw interbank spreads from 0.0 pips", "Institutional liquidity from 15+ Tier-1 banks", "No trade restrictions — all strategies allowed"]
    },
    analysis: "The math is deceptive: XM Ultra Low at 0.6 pips costs ~$6/lot (pure spread). FxPro Raw+ at 0.1 avg + $7 commission costs ~$8/lot. At low volume (1-3 lots/day), XM is actually cheaper — and the $50 entry barrier vs FxPro's $500 makes it far more accessible. But above 10 lots/day, FxPro's tighter raw spreads + cTrader tools create value beyond raw cost. The real question: do you need cTrader's professional tooling? If yes, FxPro wins. If not, XM Ultra Low saves you money."
  },
  {
    id: "pro",
    title: "The Big League: Professional & High-Net-Worth",
    subtitle: "XM Shares Account vs FxPro Elite Account — which platform handles serious money better?",
    xmAccount: {
      name: "XM Shares",
      spread: "Exchange-level real stock spreads",
      commission: "Zero commission",
      minDeposit: "$10,000",
      leverage: "None (physical stock CFD)",
      highlight: "Real stock CFDs, zero commission",
      pros: ["Commission-free stock trading on 1,200+ stocks", "Exchange-grade real pricing, no markup", "Dividend rights included on long positions", "Ideal for large capital stock allocation"]
    },
    fxproAccount: {
      name: "FxPro Elite",
      spread: "From 0.0 pips (all majors)",
      commission: "$3.50/side with volume rebates",
      minDeposit: "$30,000",
      leverage: "1:500",
      highlight: "Volume rebates — cost drops with volume",
      pros: ["Volume-based commission rebates reduce effective cost", "Dedicated account manager, priority support", "FIX API for institutional algo trading", "Custom leverage and risk parameters"]
    },
    analysis: "Two completely different animals. XM Shares targets stock CFD investors — if you want commission-free stock exposure with dividend rights, there's no competition here. FxPro Elite targets high-volume forex traders — at 50+ lots/month, volume rebates can push effective commission well below standard ECN rates. If you trade forex with serious money, FxPro Elite's rebate structure + FCA protection is the professional choice. For equity investors, XM Shares is the only game in town."
  }
];

export interface TechComparison {
  category: string;
  icon: string;
  xm: { label: string; detail: string; score: number };
  fxpro: { label: string; detail: string; score: number };
}

export const techData: TechComparison[] = [
  {
    category: "Trading Platform Options",
    icon: "ri-computer-line",
    xm: { label: "MT4 + MT5 only", detail: "Full MT4 and MT5 support with MQL community signals. TradingView integration for chart analysis. No proprietary platform and no cTrader — professional traders are limited to MetaTrader ecosystem.", score: 7.0 },
    fxpro: { label: "MT4 + MT5 + cTrader + FxPro Edge", detail: "The most complete platform lineup in the industry. cTrader with full Level 2 DOM for scalpers. FxPro Edge proprietary platform for multi-asset trading. MetaTrader suite for EA traders. Something for everyone.", score: 9.5 }
  },
  {
    category: "Copy Trading Ecosystem",
    icon: "ri-group-line",
    xm: { label: "MQL5 Community Signals", detail: "Copy trading primarily through MQL5 community signals. No dedicated proprietary copy trading platform. Functional but not specialized — you're browsing a marketplace rather than a curated platform.", score: 5.5 },
    fxpro: { label: "FxPro CopyTrade — Dedicated Platform", detail: "Purpose-built copy trading platform with transparent provider profiles, verified track records, and real-time performance metrics. Copy multiple strategies simultaneously with granular risk controls.", score: 8.5 }
  },
  {
    category: "Mobile App Experience",
    icon: "ri-smartphone-line",
    xm: { label: "Standard MT4/MT5 Mobile", detail: "Relies entirely on MetaTrader mobile apps. Solid for order management and basic charting. But no proprietary mobile experience — you get what MetaQuotes provides and nothing more.", score: 7.0 },
    fxpro: { label: "cTrader Mobile + FxPro App + MT Apps", detail: "cTrader's mobile app is widely considered the best in forex — full DOM, advanced order types, and clean design. Plus FxPro's proprietary app for account management. Three mobile options, each purpose-built.", score: 9.0 }
  },
  {
    category: "API & Automation Support",
    icon: "ri-code-line",
    xm: { label: "MQL4/MQL5 Standard API", detail: "Full MQL4/MQL5 support with rich community resources. Free VPS for qualifying EA traders. But no standalone REST API or FIX API — automation is limited to the MetaTrader ecosystem.", score: 7.0 },
    fxpro: { label: "cAlgo + FIX API + MQL Suite", detail: "cTrader's cAlgo (C#) for custom indicators and bots. FIX API for institutional-grade sub-millisecond execution. Plus full MQL support for MetaTrader. The most complete automation toolkit available.", score: 9.5 }
  }
];

export interface PerkItem {
  icon: string;
  title: string;
  description: string;
}

export const xmPerks: PerkItem[] = [
  {
    icon: "ri-gift-2-line",
    title: "$30 No-Deposit Bonus",
    description: "Claim it right after verification. Trade real markets without depositing a cent first. The ultimate risk-free test drive."
  },
  {
    icon: "ri-vip-diamond-line",
    title: "Loyalty Points Program (XMP)",
    description: "Earn points on every trade. Redeem for cash or bonus credit. The more you trade, the more you earn back — effectively lowering your spread costs."
  },
  {
    icon: "ri-cloud-line",
    title: "Free VPS Service",
    description: "Qualify with minimum deposit and volume requirements. Perfect for traders running EAs 24/7 without keeping their PC on."
  },
  {
    icon: "ri-percent-line",
    title: "100% Welcome Deposit Bonus",
    description: "Double your first deposit up to $500. Deposit $500, trade with $1,000. The most aggressive bonus in the industry."
  }
];

export const fxproPerks: PerkItem[] = [
  {
    icon: "ri-terminal-box-line",
    title: "cTrader — The Professional's Platform",
    description: "Full Depth of Market, iceberg orders, one-click trading, and advanced charting. cTrader is the platform professional scalpers and algorithmic traders choose over MT4/MT5. FxPro is one of the few brokers offering it with full ECN execution."
  },
  {
    icon: "ri-shield-star-line",
    title: "FCA Regulation + FSCS Protection",
    description: "Regulated by the UK FCA since 2006 — one of the longest track records in forex. FSCS protection up to £85,000 for UK clients. Funds held in segregated accounts at Barclays and other Tier-1 banks."
  },
  {
    icon: "ri-funds-line",
    title: "Institutional-Grade Liquidity",
    description: "Connected to 15+ Tier-1 liquidity providers including Barclays, JPMorgan, UBS, and Goldman Sachs. Massive order book means better fills, less slippage, and tighter pricing — especially for large orders."
  },
  {
    icon: "ri-customer-service-2-line",
    title: "24/5 Multilingual Support + Dedicated Managers",
    description: "Award-winning customer support available 24/5 in 20+ languages. Elite and high-volume clients get dedicated personal account managers. Phone, email, and live chat with average response under 30 seconds."
  }
];

export interface BlackSwanPoint {
  category: string;
  xm: string;
  fxpro: string;
  xmAdvantage: boolean;
}

export const blackSwanData: BlackSwanPoint[] = [
  {
    category: "Order Execution (News Events)",
    xm: "99.35% of orders executed in under 1 second. Explicit no-requote guarantee. Rock-solid during NFP, FOMC, and black-swan market events.",
    fxpro: "Institutional ECN fills under normal conditions. During extreme volatility, cTrader's DOM shows real-time depth and spreads widen to reflect market conditions. No explicit no-requote guarantee — fills reflect real liquidity.",
    xmAdvantage: true
  },
  {
    category: "Slippage Management",
    xm: "Strict slippage control with guaranteed fills at requested price or better. XM absorbs minor slippage internally rather than passing it to traders.",
    fxpro: "ECN model means fills reflect real market conditions. Positive slippage possible (price improvement), negative also occurs during fast markets. Full transparency — you see exactly what happened in your trade history.",
    xmAdvantage: true
  },
  {
    category: "Leverage Stability During Crises",
    xm: "Leverage parameters remain stable during extreme conditions. Traders can rely on pre-set risk parameters without surprise adjustments.",
    fxpro: "Leverage is generally more conservative (max 1:500 vs XM's 1:1000). During black-swan events, FxPro may temporarily reduce leverage on certain instruments as a risk management measure — but communicates changes proactively.",
    xmAdvantage: true
  },
  {
    category: "Fund Security & Compensation",
    xm: "Segregated client funds and negative balance protection across all jurisdictions. Solid multi-jurisdiction regulatory framework.",
    fxpro: "FCA regulation since 2006 provides FSCS compensation up to £85,000 for UK clients. Funds segregated at Barclays. One of the longest unblemished regulatory records in the industry (18+ years).",
    xmAdvantage: false
  }
];

export interface AccountTypeItem {
  name: string;
  highlight: string;
  spread: string;
  commission: string;
  minDeposit: string;
  leverage: string;
  note: string;
  features: string[];
  bestFor: string;
}

export const xmAccountTypes: AccountTypeItem[] = [
  {
    name: "Micro Account",
    highlight: "Ultra-low entry barrier",
    spread: "From 1.0 pip (EUR/USD)",
    commission: "Zero commission",
    minDeposit: "$5",
    leverage: "Up to 1:1000",
    note: "Contracts are 1,000 base currency units (1 micro lot). Perfect for absolute beginners who want to learn with minimal risk. Same execution quality as larger accounts — no discrimination based on account size.",
    features: [
      "Trade with as little as $5 — the industry's lowest barrier",
      "1 micro lot = 1,000 units, ideal for position sizing practice",
      "Full MT4/MT5 access with all 1,000+ instruments",
      "Negative balance protection included standard",
      "Same spreads and execution as Standard account"
    ],
    bestFor: "Complete beginners testing the waters with pocket change"
  },
  {
    name: "Standard Account",
    highlight: "Most popular — classic spread-only",
    spread: "From 1.0 pip (EUR/USD)",
    commission: "Zero commission",
    minDeposit: "$5",
    leverage: "Up to 1:1000",
    note: "XM's flagship account. 1 standard lot = 100,000 units. No commissions, just the raw spread. This is the account most XM traders use — reliable, simple, and backed by 99.35% execution rates.",
    features: [
      "Zero commission — pay only the spread, nothing more",
      "1 standard lot = 100,000 units, full-size trading",
      "Eligible for all XM bonuses including $30 no-deposit",
      "Free daily market analysis and webinars",
      "XMP loyalty points earned on every trade"
    ],
    bestFor: "The everyday trader who wants bonus perks + no commission complexity"
  },
  {
    name: "Ultra Low Account",
    highlight: "Tightest XM spreads — zero commission",
    spread: "From 0.6 pips (EUR/USD)",
    commission: "Zero commission",
    minDeposit: "$50",
    leverage: "Up to 1:1000",
    note: "XM's answer to ECN pricing — but without commissions. Spreads start at 0.6 pips on EUR/USD, nearly half the Standard account. If you trade moderate volume (3-10 lots/day), this account saves serious spread cost over time.",
    features: [
      "Spreads from 0.6 pips — XM's tightest pure-spread offering",
      "Zero commission — all cost is in the spread",
      "Standard lot size (100,000 units), full-scale trading",
      "Same 1,000+ instruments and platform access",
      "Ideal sweet spot between Micro and high-volume accounts"
    ],
    bestFor: "Cost-aware traders wanting tight spreads without commission math"
  },
  {
    name: "Shares Account",
    highlight: "Real stock CFDs — zero commission",
    spread: "Exchange-level real stock spreads",
    commission: "Per-share commission (varies by exchange)",
    minDeposit: "$10,000",
    leverage: "No leverage",
    note: "Not a forex account — this is for stock investors. Trade 1,200+ global stocks with exchange-grade pricing. Commissions are charged per share and vary by exchange market. You receive dividend adjustments on long positions. No leverage means you're trading at full notional value.",
    features: [
      "Trade 1,200+ global stocks across US, UK, EU exchanges",
      "Exchange-grade pricing with transparent per-share commissions",
      "Dividend rights included on long positions",
      "Access to US, UK, EU, and Asian stock exchanges",
      "Actual share trading — different from index CFDs"
    ],
    bestFor: "Stock investors wanting real exchange access, not CFD exposure"
  },
];

export const fxproAccountTypes: AccountTypeItem[] = [
  {
    name: "Standard Account",
    highlight: "Spread-only — FxPro's entry tier",
    spread: "From 1.2 pips (EUR/USD)",
    commission: "Zero commission",
    minDeposit: "$100",
    leverage: "Up to 1:500",
    note: "FxPro's entry-level account. Zero commission with spreads from 1.2 pips on major pairs. Access to all platforms including cTrader, MT4, MT5, and FxPro Edge. A solid starting point with institutional-grade execution backbone — no dealing desk, ever.",
    features: [
      "Zero commission — straightforward spread-only pricing",
      "Full cTrader + MT4 + MT5 + FxPro Edge access",
      "Institutional execution quality at entry-level pricing",
      "Islamic swap-free version available on request",
      "All trading strategies fully permitted — no restrictions"
    ],
    bestFor: "Traders wanting FxPro's platform ecosystem at the lowest entry point"
  },
  {
    name: "Raw+ Account",
    highlight: "True ECN — FxPro's flagship",
    spread: "From 0.0 pips, avg 0.1-0.2 (EUR/USD)",
    commission: "$3.50 per side ($7 round-turn per lot)",
    minDeposit: "$500",
    leverage: "Up to 1:500",
    note: "FxPro's most popular account — and for good reason. True ECN execution with raw interbank spreads from 0.0 pips. Full cTrader suite with Level 2 Depth of Market and iceberg orders. This is where FxPro's institutional liquidity advantage really shows.",
    features: [
      "Raw interbank spreads from 0.0 pips — true ECN pricing",
      "$3.50/side commission — competitive mid-tier rate",
      "cTrader with full DOM and advanced order types",
      "Institutional liquidity from 15+ Tier-1 banks",
      "FIX API available for algorithmic traders"
    ],
    bestFor: "Active scalpers and day traders who want the full cTrader ECN experience"
  },
  {
    name: "Elite Account",
    highlight: "Volume-based commission rebates",
    spread: "From 0.0 pips (EUR/USD)",
    commission: "$3.50/side with volume rebates",
    minDeposit: "$30,000",
    leverage: "Up to 1:500",
    note: "Built for professional traders moving serious volume. Commission starts at standard $3.50/side but scales down with volume-based rebates — the more you trade, the lower your effective cost per lot. Dedicated account manager, priority withdrawals, and FIX API access. At 50+ lots/month, rebates can significantly reduce your all-in trading costs.",
    features: [
      "Volume-based commission rebates — effective rate drops as volume grows",
      "Dedicated personal account manager for white-glove service",
      "FIX API for institutional-grade algorithmic trading",
      "Priority withdrawals — processed within same business day",
      "Custom leverage and risk parameter configuration"
    ],
    bestFor: "Professional traders doing 50+ lots/month who want rebate-driven pricing"
  },
  {
    name: "cTrader Account",
    highlight: "Dedicated ECN — raw spreads + cTrader",
    spread: "From 0.0 pips, avg 0.0-0.2 (EUR/USD)",
    commission: "$3.50 per side ($7 round-turn per lot)",
    minDeposit: "$100",
    leverage: "Up to 1:500",
    note: "FxPro's dedicated ECN account built exclusively for the cTrader platform. Raw interbank spreads from 0.0 pips with transparent $35/million commission. Purpose-built for scalpers and algorithmic traders who demand full Depth of Market, iceberg orders, and one-click trading — all inside cTrader's professional ecosystem.",
    features: [
      "Exclusively on cTrader platform — built for pro scalping workflow",
      "Raw interbank spreads from 0.0 pips with full Level 2 DOM",
      "Transparent $35/million commission ($3.50/side per lot)",
      "Iceberg orders, one-click trading, advanced order types",
      "cAlgo (C#) for custom indicators and automated trading bots"
    ],
    bestFor: "Active scalpers and cTrader loyalists who want the full ECN experience"
  }
];

export interface QuizQuestion {
  id: string;
  question: string;
  options: { label: string; value: string }[];
}

export const quizQuestions: QuizQuestion[] = [
  {
    id: "capital",
    question: "What's your starting capital?",
    options: [
      { label: "Under $100 — Just testing the waters", value: "small" },
      { label: "$100 - $5,000 — Got a decent budget", value: "medium" },
      { label: "$5,000+ — Professional level", value: "large" }
    ]
  },
  {
    id: "style",
    question: "What's your trading style?",
    options: [
      { label: "Scalping / High-frequency — Dozens to hundreds of trades daily", value: "scalping" },
      { label: "Day trading — A few trades per day", value: "daytrade" },
      { label: "Swing trading / Running EAs — Holding days to weeks", value: "swing" }
    ]
  },
  {
    id: "priority",
    question: "What matters most to you?",
    options: [
      { label: "Lowest possible trading costs — every pip counts", value: "cost" },
      { label: "Bonuses, promotions, and free money to boost my account", value: "bonus" },
      { label: "Best platform tools, professional execution, and FCA regulation", value: "platform" }
    ]
  }
];

export function computeQuizResult(answers: Record<string, string>): { broker: "xm" | "fxpro"; percentage: number; reason: string } {
  const { capital, style, priority } = answers;
  let xmScore = 0;
  let fxproScore = 0;

  // Trading style scoring
  if (style === "scalping") {
    fxproScore += 35;
    xmScore += 10;
  } else if (style === "daytrade") {
    fxproScore += 20;
    xmScore += 20;
  } else {
    xmScore += 20;
    fxproScore += 10;
  }

  // Capital scoring
  if (capital === "small") {
    xmScore += 35;
    fxproScore += 5;
  } else if (capital === "large") {
    fxproScore += 25;
    xmScore += 10;
  } else {
    xmScore += 15;
    fxproScore += 15;
  }

  // Priority scoring
  if (priority === "cost") {
    fxproScore += 20;
    xmScore += 10;
  } else if (priority === "bonus") {
    xmScore += 35;
    fxproScore += 0;
  } else {
    fxproScore += 30;
    xmScore += 5;
  }

  const total = xmScore + fxproScore;
  if (xmScore >= fxproScore) {
    return {
      broker: "xm",
      percentage: Math.round((xmScore / total) * 100),
      reason: xmScore > fxproScore * 1.2
        ? "Your trading style is a strong match for XM: low entry barrier, generous bonuses, and rock-solid execution. Perfect for beginners, bonus hunters, and traders who value accessibility over platform sophistication."
        : "You lean toward XM, but FxPro is worth considering for its cTrader platform and FCA regulation. Start with XM's $30 no-deposit bonus to test the waters before committing larger capital."
    };
  }
  return {
    broker: "fxpro",
    percentage: Math.round((fxproScore / total) * 100),
    reason: fxproScore > xmScore * 1.3
      ? "Your trading style is built for FxPro: institutional-grade execution, cTrader's professional tooling, and FCA regulation since 2006. Scalpers and platform-focused traders will find their optimal setup here."
      : "You lean toward FxPro, but XM's bonuses and lower minimum deposit are worth considering. Try FxPro's cTrader Raw+ account for the industry's best professional trading experience."
  };
}